import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
];

export const LanguageToggle = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = i18n.language?.startsWith('zh') ? 'zh' : 'en';

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (code) => {
    setOpen(false);
    if (code !== current) navigate(`/${code}`);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        data-testid="language-toggle"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-lg border transition-colors duration-200 active:scale-[0.97]"
        style={{
          color: 'var(--vc-fg-muted)',
          borderColor: open ? 'rgba(214,168,74,0.4)' : 'rgba(255,255,255,0.12)',
          backgroundColor: 'transparent',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(214,168,74,0.4)'; e.currentTarget.style.color = 'var(--vc-gold)'; }}
        onMouseLeave={(e) => { if (!open) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'var(--vc-fg-muted)'; } }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {LANGS.find((l) => l.code === current)?.label}
        <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          data-testid="language-dropdown"
          role="listbox"
          className="absolute right-0 mt-1.5 w-32 rounded-lg overflow-hidden"
          style={{
            backgroundColor: 'rgba(7,21,37,0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 200,
          }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === current}
              data-testid={`lang-option-${l.code}`}
              onClick={() => pick(l.code)}
              className="w-full text-left px-3.5 py-2.5 text-xs font-medium transition-colors duration-150"
              style={{
                color: l.code === current ? 'var(--vc-gold)' : 'var(--vc-fg-muted)',
                backgroundColor: 'transparent',
                fontFamily: 'Inter, sans-serif',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
